import { createContext, useContext, useMemo, useState } from "react";

// NOTE: this file contains ONLY user-facing UI translation strings (EN/FR).
// Keys named "password", "email" etc. are label text, not credentials or API
// keys — no secrets live in the frontend; auth uses httpOnly session cookies.
const translations = {
  en: {
    dashboard: "Dashboard", buyers: "Buyers", sellers: "Sellers", properties: "Properties",
    viewings: "Viewings", aiGenerator: "AI Generator", templates: "Templates", tasks: "Tasks",
    pipeline: "Pipeline", diary: "Diary", leads: "Leads", rooms: "Rooms",
    // Quick add
    qaNote: "Note", qaTask: "Task", qaLead: "Lead", qaBuyer: "Buyer", qaSeller: "Seller",
    qaProperty: "Property", qaViewing: "Viewing", qaRoom: "Room measurement",
    qaEstimation: "Estimation",
    takePhoto: "Camera",
    share: "Share",
    // Offline / sync
    syncOffline: "Offline", syncSyncing: "Syncing…", syncPending: "{n} to sync",
    syncSynced: "Synced", syncOnline: "Online",
    conflictTitle: "Sync conflict",
    conflictExplain: "This record was changed on the server while you were offline. Choose which version to keep:",
    conflictField: "Field", mineLabel: "My version", serverLabel: "Server version",
    keepMine: "Keep my version", keepServer: "Keep server version",
    recordDeletedOnServer: "This record was deleted on the server. Keeping your version will recreate it.",
    appUpdated: "New version installed",
    measurementsLabel: "Measurements (length × width)",
    addMeasurement: "Add measurement",
    measurementsTotalHint: "Sum of all measurements",
    propertyViewingsSubtitle: "Every viewing booked at this property.",
    fileDownloaded: "Sharing not available here — file downloaded instead",
    emailFiles: "Send by email",
    shareEmailTitle: "Send by email",
    recipient: "Recipient",
    message: "Message",
    sendEmail: "Send",
    emailSent: "Email sent",
    selectFilesToSend: "Attachments",
    selectAll: "Select all",
    validEmailRequired: "Enter a valid recipient email",
    shareDefaultMessage: "Hello,\n\nPlease find attached the documents for {name}.\n\nKind regards,",
    estimationsTitle: "Estimations",
    estimationsSubtitle: "Every estimation dossier, attached to its property. Open one or start a new dossier.",
    startEstimation: "Start a dossier…",
    noEstimations: "No estimation dossiers yet. Start one for a property above.",
    dateOfBirth: "Date of birth", placeOfBirth: "Place of birth", countryOfBirth: "Country of birth",
    nationality: "Nationality", address: "Address", preferredContact: "Preferred contact",
    city: "City", postcode: "Postcode", estimatedValue: "Estimated value",
    // Diary
    diaryTitle: "Working diary",
    diarySubtitle: "Every call, note, and appointment in one chronological view.",
    diarySearchPlaceholder: "Search diary…",
    allTypes: "All types", addEntry: "Add entry", editEntry: "Edit entry",
    noDiaryEntries: "No diary entries yet. Log your first call or note.",
    diaryType_note: "Note", diaryType_call: "Call", diaryType_email: "Email",
    diaryType_meeting: "Meeting", diaryType_viewing: "Viewing", diaryType_appointment: "Appointment",
    dateTime: "Date & time", type: "Type", subject: "Subject",
    subjectPlaceholder: "e.g. Left voicemail",
    linkedBuyer: "Linked buyer", linkedSeller: "Linked seller",
    linkedProperty: "Linked property", linkedLead: "Linked lead",
    followUpRequired: "Follow-up required",
    diaryAutoTaskHint: "A task will be auto-created on your Tasks list.",
    none: "None",
    // Leads
    leadsTitle: "Leads",
    leadsSubtitle: "Track every enquiry from first contact to next action.",
    leadsSearchPlaceholder: "Search leads…",
    addLead: "Add lead", editLead: "Edit lead",
    noLeads: "No leads yet. Add your first enquiry.",
    leadName: "Lead name", leadNamePlaceholder: "e.g. Website enquiry — Paris family",
    leadNameRequired: "Lead name is required",
    source: "Source", stage: "Stage", priority: "Priority",
    nextAction: "Next action", nextFollowUpDate: "Next follow-up date",
    allPriorities: "All priorities",
    priority_low: "Low", priority_medium: "Medium", priority_high: "High",
    source_website: "Website", source_referral: "Referral", source_portal: "Portal",
    source_social: "Social", source_walk_in: "Walk-in", source_phone: "Phone", source_other: "Other",
    leadStage_new: "New", leadStage_qualified: "Qualified",
    leadStage_appointment: "Appointment", leadStage_viewing: "Viewing",
    leadStage_negotiation: "Negotiation", leadStage_won: "Won", leadStage_lost: "Lost",
    buyer: "Buyer", seller: "Seller", property: "Property",
    createNew: "Create new {label}",
    createNewPromptLabel: "Enter the name for the new {label}:",
    created: "Created",
    convert: "Convert",
    toBuyer: "→ Buyer",
    toSeller: "→ Seller",
    convertToBuyer: "Convert to buyer",
    convertToSeller: "Convert to seller",
    confirmConvertToBuyer: "Move this lead into your Buyers list? The lead will be removed from Leads.",
    confirmConvertToSeller: "Move this lead into your Sellers list? The lead will be removed from Leads.",
    convertedToBuyer: "Lead moved to Buyers",
    convertedToSeller: "Lead moved to Sellers",
    timelineTab: "Activity log",
    timelineEmpty: "No notes yet. Log a call, meeting or message above.",
    roomsScheduleTitle: "Rooms schedule",
    subtotal: "Subtotal",
    totalHabitable: "Total habitable area",
    totalExcluded: "Total excluded from habitable",
    totalArea: "Total area",
    excludedFromHabitable: "Excluded from habitable",
    excludeFromHabitable: "Exclude from habitable area",
    excludeFromHabitableHint: "Tick this to keep the room in the schedule but out of the total habitable area (e.g. low ceiling, no heating, unfinished attic).",
    roomCategory: "Category",
    roomCategory_habitable: "Habitable rooms",
    roomCategory_attic: "Attic / loft",
    roomCategory_basement: "Basement / cellar",
    roomCategory_garage: "Garage",
    roomCategory_outbuilding: "Outbuilding",
    roomCategory_terrace: "Terrace / balcony",
    roomCategory_other: "Other",
    printSchedule: "Print / PDF",
    popupBlocked: "Please allow pop-ups for this site so the print window can open.",
    roomsTab: "Rooms",
    parcelsTab: "Cadastral",
    dossierTab: "Estimation",
    dossierNeedsProperty: "The estimation dossier is attached to a property. Link a property to start the dossier.",
    dossierLinkedProperty: "Linked property",
    addParcel: "Add cadastral",
    editParcel: "Edit cadastral",
    noParcelsForProperty: "No cadastral entries yet. Add the first one below.",
    parcelsForProperty: "Cadastral entries for this property",
    section: "Section",
    parcelNumber: "Parcel number",
    lieuDit: "Lieu-dit (locality name)",
    commune: "Commune",
    surface: "Surface",
    totalCadastralSurface: "Total cadastral surface",
    ref: "Ref",
    viewCards: "Cards",
    viewTable: "Table",
    noRoomsForProperty: "No rooms measured yet. Add the first one below.",
    roomsForProperty: "Rooms measured for this property",
    roomsMovedTitle: "Open a property to measure its rooms",
    roomsMovedBody: "Room measurements now live inside each property (Properties → open the property → Rooms tab). This keeps every schedule attached to its property and lets you print it in one click.",
    openProperties: "Open Properties",
    documents: "Documents",
    documentsHint: "Upload mandate, DPE, ERP, diagnostics, deed, ID, etc.",
    idDocuments: "ID documents",
    idDocumentsHint: "Passport, driving licence, national ID — kept private.",
    addDocument: "Add document",
    addNote: "Add note",
    noteSubjectPlaceholder: "Subject (optional) — e.g. Called about the roof",
    noteBodyPlaceholder: "What happened? Anything to remember…",
    noteBodyRequired: "Please write a note first",
    noteAdded: "Note added",
    followUpRequired: "Follow up needed",
    followUpOn: "Follow up on",
    openBuyer: "Open buyer",
    openSeller: "Open seller",
    alreadyLinkedBuyer: "Already linked to a buyer",
    alreadyLinkedSeller: "Already linked to a seller",
    // Rooms
    roomsTitle: "Room measurements",
    roomsSubtitle: "Length × width = area, calculated automatically.",
    roomsSearchPlaceholder: "Search rooms…",
    addRoom: "Add measurement", editRoom: "Edit measurement",
    noRooms: "No measurements yet. Add your first room during a property visit.",
    roomName: "Room name", roomNamePlaceholder: "e.g. Master bedroom",
    roomNameRequired: "Room name is required",
    propertyRequired: "Please choose a property", selectProperty: "Select a property",
    allProperties: "All properties",
    length: "Length", width: "Width", unit: "Unit",
    lengthWidthRequired: "Both length and width are required",
    calculatedArea: "Calculated area",
    areaFormula: "Area = Length × Width",
    manualOverrideActive: "Using manual area — overrides length × width",
    manualAreaOverride: "Total area (manual override)",
    manualAreaHint: "Type a number here to override the calculation — useful for irregular rooms or when you already have the surveyor's figure.",
    measurementDate: "Measurement date",
    totalAreaByProperty: "Total measured area by property",
    pipelineTitle: "Property pipeline",
    pipelineSubtitle: "Drag a card across stages to advance a deal. AI will suggest what to do next.",
    pipelineSearchPlaceholder: "Search by property, address, ref…",
    allStages: "All stages", allNegotiators: "All negotiators", clearFilters: "Clear filters",
    stageEmpty: "Drop a property here", stageMoved: "Stage updated",
    aiSuggestNext: "Suggest next action", aiSuggestTitle: "Next action",
    noAddress: "Untitled property", offersShort: "offers", loading: "Loading",
    lostReasonPrompt: "Optional — reason this deal was lost (e.g. chose another agent, withdrew, price):",
    completionDatePrompt: "Completion date (YYYY-MM-DD):",
    offersTab: "Offers", offersTitle: "Offers on this property",
    offersSubtitle: "Track every offer received. Accepting one moves the property to Under Offer and rejects the rest.",
    addOffer: "Add offer", offerAdded: "Offer added",
    offerAmount: "Offer amount", offerDate: "Date received",
    financialPosition: "Financial position", chainPosition: "Chain position",
    proofOfFunds: "Proof of funds received", noOffersYet: "No offers yet on this property.",
    selectBuyer: "Select a buyer", buyer: "Buyer",
    offerAmountRequired: "Please enter an offer amount",
    acceptOffer: "Accept this offer",
    acceptOfferConfirm: "Accept the offer of {amount}? This will move the property to Under Offer and reject other pending offers.",
    offerAccepted: "Offer accepted — property moved to Under Offer.",
    offerStatus_new: "New", offerStatus_negotiating: "Negotiating",
    offerStatus_accepted: "Accepted", offerStatus_rejected: "Rejected", offerStatus_withdrawn: "Withdrawn",
    fin_cash: "Cash", fin_mortgage_agreed: "Mortgage agreed", fin_mortgage: "Mortgage pending", fin_unknown: "Unknown",
    finCash: "Cash", finMortgageAgreed: "Mortgage agreed", finMortgage: "Mortgage pending", finUnknown: "Unknown",
    chain_no_chain: "No chain", chain_top_of_chain: "Top of chain", chain_in_chain: "In chain", chain_unknown: "Unknown",
    chainNone: "No chain", chainTop: "Top of chain", chainIn: "In chain",
    kpiNewLeads: "New vendor leads", kpiContactMade: "Contact made",
    kpiValuationsBooked: "Valuations booked", kpiValuationsCompleted: "Valuations completed",
    kpiAwaitingDecision: "Awaiting decision", kpiMandatesSigned: "Mandates signed",
    kpiClosedWon: "Closed won", kpiLiveListings: "Live listings",
    kpiViewingsToday: "Viewings today", kpiActiveOffers: "Active offers",
    kpiUnderOffer: "Under offer", kpiSoldThisMonth: "Sold this month", kpiClosedLost: "Closed lost",
    search: "Search...", add: "Add", edit: "Edit", delete: "Delete", save: "Save", preview: "Preview",
    cancel: "Cancel", copy: "Copy", copied: "Copied to clipboard", close: "Close",
    logout: "Sign out", login: "Sign in", email: "Email", password: "Password",
    welcomeBack: "Welcome back", loginSubtitle: "Your personal real estate assistant",
    continueWithGoogle: "Continue with Google",
    signInDescription: "Sign in with your Google account to access your workspace.",
    signInFooter: "New here? Your account will be created automatically on first sign-in.",
    signInTab: "Sign in", signUpTab: "Sign up",
    signIn: "Sign in", signingIn: "Signing in...", or: "or",
    yourName: "Your name", yourNamePlaceholder: "Julia Martin",
    createAccount: "Create account", creatingAccount: "Creating account...",
    accountCreated: "Welcome to Julia AI",
    passwordHint: "At least 8 characters", passwordTooShort: "Password must be at least 8 characters.",
    passwordsDontMatch: "Passwords don't match.",
    forgotPassword: "Forgot password?",
    forgotTitle: "Reset password", forgotSubtitle: "Enter your email and we'll send you a link to set a new password.",
    sendResetLink: "Send reset link", sending: "Sending...", back: "Back",
    checkYourInbox: "Check your inbox", resetSentBody: "If an account exists for that email, a reset link is on its way. The link expires in 1 hour.",
    backToSignIn: "Back to sign in",
    resetTitle: "Choose a new password", resetSubtitle: "Set the password you'll use to sign in.",
    newPassword: "New password", confirmPassword: "Confirm password",
    updatePassword: "Update password", updating: "Updating...", passwordUpdated: "Password updated",
    resetLinkInvalid: "This reset link is missing or invalid.",
    profile: "Profile", profileSubtitle: "Your account and email signature",
    googleAccount: "Google account", displayName: "Display name",
    agentSignature: "Email signature", agentSignaturePlaceholder: "Julia Martin\nAgent immobilier indépendant\njulia@example.com · +33 6 12 34 56 78",
    agentSignatureHint: "Used when [AgentName] or [AgentSignature] appears in a template.",
    todaysAppointments: "Today's appointments", urgentFollowUps: "Urgent follow-ups",
    upcomingTasks: "Upcoming tasks", noPendingTasks: "You're all caught up",
    noDueDate: "No due date", viewAllTasks: "View all tasks",
    missingDocuments: "Missing documents", upcomingDecisions: "Upcoming decisions",
    newEnquiries: "New buyer enquiries", commissionPipeline: "Commission pipeline",
    live: "Live", underOffer: "Under offer", nothingToday: "Nothing scheduled today",
    allClear: "All clear", noResults: "No results found",
    name: "Name", phone: "Phone", country: "Country", budget: "Budget", notes: "Notes",
    financePosition: "Finance position", searchArea: "Search area", mustHaves: "Must-haves",
    dealBreakers: "Deal breakers", likesDislikes: "Likes & dislikes", nextAction: "Next action",
    nextActionDate: "Next action date", autoTaskHint: "A task with this due date will be created automatically",
    followUpDate: "Follow-up date", addBuyer: "Add buyer", editBuyer: "Edit buyer",
    cash: "Cash", mortgage: "Mortgage", propertyToSell: "Property to sell", unknown: "Unknown",
    status: "Status", lastContact: "Last contact", documentsChecklist: "Documents checklist",
    linkedProperty: "Linked property", addSeller: "Add seller", editSeller: "Edit seller",
    prospect: "Lead", valuationBooked: "Valuation booked", valuationCompleted: "Valuation completed",
    mandatePending: "Mandate pending", sold: "Sold",
    addProperty: "Add property", editProperty: "Edit property", priceNet: "Price net vendeur",
    agencyFees: "Agency fees", priceTotal: "Price incl. fees", propertyType: "Type",
    bedrooms: "Bedrooms", bathrooms: "Bathrooms", habitableSpace: "Habitable space (m²)",
    landSize: "Land (m²)", outbuildings: "Outbuildings", pool: "Pool", gitePotential: "Gîte potential",
    heating: "Heating", drainage: "Drainage", locationPublic: "Public location description",
    addressPrivate: "Private address / access notes", advertEn: "English advert", advertFr: "French advert",
    photos: "Photos", floorplans: "Floorplans", uploadPhoto: "Upload photo", uploadFloorplan: "Upload floorplan",
    newViewing: "New viewing feedback", editViewing: "Edit viewing", buyer: "Buyer", property: "Property",
    dateTime: "Date & time", generalReaction: "General reaction", liked: "What they liked",
    concerns: "Concerns / reservations", otherProperties: "Other properties still to view",
    decisionDate: "Decision date", generateReport: "Generate Viewing Report", generating: "Generating...",
    englishReport: "English report", frenchReport: "French report", hestiaNotes: "Hestia notes",
    reportReady: "Report generated — follow-up task created", viewReport: "View report",
    aiTitle: "AI Report Generator", aiSubtitle: "Paste rough notes, pick an output, and let Julia AI write it",
    roughNotes: "Your rough notes", outputType: "Output type", generate: "Generate",
    viewingReport: "Viewing report", sellerFollowUp: "Seller follow-up", buyerFollowUp: "Buyer follow-up",
    propertyAdvert: "Property advert", frenchTranslation: "French translation",
    englishTranslation: "English translation", whatsappMessage: "WhatsApp message", formalEmail: "Formal email",
    result: "Result", addTemplate: "Add template", editTemplate: "Edit template",
    titleEn: "Title (English)", titleFr: "Title (French)", bodyEn: "English text", bodyFr: "French text",
    category: "Category", addTask: "Add task", editTask: "Edit task", taskTitle: "Task title",
    templatesSubtitle: "Reusable messages saved to your database — never overwritten by redeploys",
    duplicate: "Duplicate", duplicated: "Template duplicated", updated: "Updated",
    status_active: "Active", status_draft: "Draft", status_archived: "Archived",
    templateValidationTitle: "English title is required",
    templateValidationBody: "At least one language (EN or FR) must have content",
    confirmDeleteTemplate: "Delete template \"{name}\"? This cannot be undone.",
    autoTranslateLabel: "Auto-translate on save",
    translating: "Translating...",
    translateFromEn: "Translate to FR",
    translateFromFr: "Translate to EN",
    translatedNotice: "Translated to {tgt}",
    translateFailed: "Translation failed",
    sourceLangHint: "Editing in {src} — the {tgt} version will be regenerated when you save.",
    lang_en: "English", lang_fr: "French",
    systemDefault: "System default",
    systemTemplateHint: "Editing a system default creates a private copy — original stays untouched.",
    insertPlaceholder: "+ Insert placeholder",
    dueDate: "Due date", priority: "Priority", linkedRecord: "Linked record", linkedType: "Link to",
    pending: "Pending", done: "Done", overdue: "Overdue", high: "High", medium: "Medium", low: "Low",
    none: "None", viewing: "Viewing", seller: "Seller", today: "Today", decisionBy: "Decision by",
    lead: "Lead",
    saved: "Saved", deleted: "Deleted", confirmDelete: "Delete this record?", required: "This field is required",
    viewingHistory: "Viewing history", buyerMatches: "Potential buyer matches", noViewings: "No viewings recorded yet",
    firstName: "First name", surname: "Surname",
    forms: "Forms", formBuilder: "Form Builder", newForm: "New form", fillForm: "Fill in", formName: "Form name",
    addField: "Add field", fieldLabel: "Field label", fieldType: "Type", requiredField: "Required",
    optionsHint: "Options (comma-separated)", typeText: "Text", typeTextarea: "Long text", typeNumber: "Number",
    typeDate: "Date", typeDropdown: "Dropdown", typeCheckbox: "Checkbox", typeFile: "File upload",
    templatesTab: "Form templates", submissionsTab: "Submissions", copySummary: "Copy summary",
    noSubmissions: "No submissions yet", submitForm: "Save submission",
    sellerDetailsCat: "Seller details", propertyDetailsCat: "Property details", buyerCriteriaCat: "Buyer criteria",
    viewingFeedbackCat: "Viewing feedback", customCat: "Custom",
    rooms: "Rooms", roomMeasurements: "Room measurements", addRoom: "Add room", editRoom: "Edit room",
    roomName: "Room name", floorLevel: "Floor", shapeType: "Shape", ceilingHeight: "Ceiling height (m)",
    flooring: "Flooring", windowsGlazing: "Windows / glazing", builtInStorage: "Built-in storage", condition: "Condition",
    sectionsLabel: "Measurement sections", addSection: "Add section", sectionName: "Section name",
    lengthM: "Length (m)", widthM: "Width (m)", areaLabel: "Area", totalArea: "Total area",
    overrideArea: "Override total (m²)", sketchRoom: "Sketch", drawRoom: "Draw room", finishRoom: "Finish room",
    undo: "Undo", redo: "Redo", clearSketch: "Clear", perimeter: "Perimeter", habitableArea: "Habitable area",
    excludeHabitable: "Exclude from habitable area", needsVerification: "Needs verification",
    applyToProperty: "Apply to property record", copySchedule: "Copy schedule",
    roomSchedule: "Room schedule",
    tapToDraw: "Tap or click to place each corner, then press Finish room",
    tapWallHint: "Tap a wall to enter its measured length", deleteCorner: "Delete corner",
    notClosed: "Walls don't quite close — check the lengths",
    sellingPoints: "Key selling points", summary: "Summary", descriptionLabel: "Description", marketing: "Marketing",
    generateAdvert: "Generate EN + FR adverts (AI)", advertsGenerated: "Adverts generated from rooms & selling points",
    saveFirst: "Save the property first",
    calendar: "Calendar", todayBtn: "Today", addToCalendar: "Add to calendar",
    followUpEvent: "Follow-up", decisionEvent: "Decision", moreEvents: "more", time: "Time",
  },
  fr: {
    dashboard: "Tableau de bord", buyers: "Acheteurs", sellers: "Vendeurs", properties: "Biens",
    viewings: "Visites", aiGenerator: "Générateur IA", templates: "Modèles", tasks: "Tâches",
    pipeline: "Pipeline", diary: "Journal", leads: "Prospects", rooms: "Pièces",
    qaNote: "Note", qaTask: "Tâche", qaLead: "Prospect", qaBuyer: "Acheteur", qaSeller: "Vendeur",
    qaProperty: "Bien", qaViewing: "Visite", qaRoom: "Métré",
    qaEstimation: "Estimation",
    takePhoto: "Photo",
    share: "Partager",
    // Offline / sync
    syncOffline: "Hors ligne", syncSyncing: "Synchronisation…", syncPending: "{n} à synchroniser",
    syncSynced: "Synchronisé", syncOnline: "En ligne",
    conflictTitle: "Conflit de synchronisation",
    conflictExplain: "Cette fiche a été modifiée sur le serveur pendant que vous étiez hors ligne. Choisissez la version à conserver :",
    conflictField: "Champ", mineLabel: "Ma version", serverLabel: "Version serveur",
    keepMine: "Garder ma version", keepServer: "Garder la version serveur",
    recordDeletedOnServer: "Cette fiche a été supprimée sur le serveur. Garder votre version la recréera.",
    appUpdated: "Nouvelle version installée",
    measurementsLabel: "Mesures (longueur × largeur)",
    addMeasurement: "Ajouter une mesure",
    measurementsTotalHint: "Somme de toutes les mesures",
    propertyViewingsSubtitle: "Toutes les visites organisées pour ce bien.",
    fileDownloaded: "Partage indisponible ici — fichier téléchargé",
    emailFiles: "Envoyer par email",
    shareEmailTitle: "Envoyer par email",
    recipient: "Destinataire",
    message: "Message",
    sendEmail: "Envoyer",
    emailSent: "Email envoyé",
    selectFilesToSend: "Pièces jointes",
    selectAll: "Tout sélectionner",
    validEmailRequired: "Saisissez un email de destinataire valide",
    shareDefaultMessage: "Bonjour,\n\nVeuillez trouver ci-joint les documents concernant {name}.\n\nCordialement,",
    estimationsTitle: "Estimations",
    estimationsSubtitle: "Tous les dossiers d'estimation, rattachés à leur bien. Ouvrez un dossier ou commencez-en un nouveau.",
    startEstimation: "Commencer un dossier…",
    noEstimations: "Aucun dossier d'estimation. Commencez-en un pour un bien ci-dessus.",
    dateOfBirth: "Date de naissance", placeOfBirth: "Lieu de naissance", countryOfBirth: "Pays de naissance",
    nationality: "Nationalité", address: "Adresse", preferredContact: "Contact préféré",
    city: "Ville", postcode: "Code postal", estimatedValue: "Valeur estimée",
    diaryTitle: "Journal de travail",
    diarySubtitle: "Chaque appel, note et rendez-vous dans une seule vue chronologique.",
    diarySearchPlaceholder: "Rechercher dans le journal…",
    allTypes: "Tous les types", addEntry: "Ajouter une entrée", editEntry: "Modifier l'entrée",
    noDiaryEntries: "Aucune entrée pour l'instant. Enregistrez votre premier appel ou note.",
    diaryType_note: "Note", diaryType_call: "Appel", diaryType_email: "Email",
    diaryType_meeting: "Réunion", diaryType_viewing: "Visite", diaryType_appointment: "Rendez-vous",
    dateTime: "Date & heure", type: "Type", subject: "Sujet",
    subjectPlaceholder: "ex. Message vocal laissé",
    linkedBuyer: "Acheteur lié", linkedSeller: "Vendeur lié",
    linkedProperty: "Bien lié", linkedLead: "Prospect lié",
    followUpRequired: "Suivi requis",
    diaryAutoTaskHint: "Une tâche sera créée automatiquement dans votre liste.",
    none: "Aucun",
    leadsTitle: "Prospects",
    leadsSubtitle: "Suivez chaque demande depuis le premier contact.",
    leadsSearchPlaceholder: "Rechercher des prospects…",
    addLead: "Ajouter un prospect", editLead: "Modifier le prospect",
    noLeads: "Aucun prospect pour l'instant. Ajoutez votre première demande.",
    leadName: "Nom du prospect", leadNamePlaceholder: "ex. Demande site — famille Paris",
    leadNameRequired: "Le nom du prospect est requis",
    source: "Source", stage: "Étape", priority: "Priorité",
    nextAction: "Action suivante", nextFollowUpDate: "Prochaine relance",
    allPriorities: "Toutes les priorités",
    priority_low: "Basse", priority_medium: "Moyenne", priority_high: "Haute",
    source_website: "Site web", source_referral: "Recommandation", source_portal: "Portail",
    source_social: "Réseaux sociaux", source_walk_in: "Passage", source_phone: "Téléphone", source_other: "Autre",
    leadStage_new: "Nouveau", leadStage_qualified: "Qualifié",
    leadStage_appointment: "Rendez-vous", leadStage_viewing: "Visite",
    leadStage_negotiation: "Négociation", leadStage_won: "Gagné", leadStage_lost: "Perdu",
    buyer: "Acheteur", seller: "Vendeur", property: "Bien",
    createNew: "Créer un(e) nouveau/nouvelle {label}",
    createNewPromptLabel: "Entrez le nom pour le/la nouveau/nouvelle {label} :",
    created: "Créé",
    convert: "Convertir",
    toBuyer: "→ Acheteur",
    toSeller: "→ Vendeur",
    convertToBuyer: "Convertir en acheteur",
    convertToSeller: "Convertir en vendeur",
    confirmConvertToBuyer: "Déplacer ce prospect dans la liste Acheteurs ? Il sera supprimé des Prospects.",
    confirmConvertToSeller: "Déplacer ce prospect dans la liste Vendeurs ? Il sera supprimé des Prospects.",
    convertedToBuyer: "Prospect déplacé vers les Acheteurs",
    convertedToSeller: "Prospect déplacé vers les Vendeurs",
    timelineTab: "Journal d'activité",
    timelineEmpty: "Aucune note pour l'instant. Ajoutez un appel, une rencontre ou un message ci-dessus.",
    roomsScheduleTitle: "Métrage des pièces",
    subtotal: "Sous-total",
    totalHabitable: "Surface habitable totale",
    totalExcluded: "Total non habitable",
    totalArea: "Surface totale",
    excludedFromHabitable: "Hors surface habitable",
    excludeFromHabitable: "Exclure de la surface habitable",
    excludeFromHabitableHint: "Cochez pour garder la pièce dans le métrage mais l'exclure du total habitable (ex. hauteur sous plafond insuffisante, non chauffée, comble non aménagé).",
    roomCategory: "Catégorie",
    roomCategory_habitable: "Pièces habitables",
    roomCategory_attic: "Combles / grenier",
    roomCategory_basement: "Cave / sous-sol",
    roomCategory_garage: "Garage",
    roomCategory_outbuilding: "Dépendance",
    roomCategory_terrace: "Terrasse / balcon",
    roomCategory_other: "Autre",
    printSchedule: "Imprimer / PDF",
    popupBlocked: "Autorisez les fenêtres pop-up pour ce site afin d'ouvrir l'aperçu d'impression.",
    roomsTab: "Pièces",
    parcelsTab: "Cadastre",
    dossierTab: "Estimation",
    dossierNeedsProperty: "Le dossier d'estimation est rattaché à un bien. Liez un bien pour commencer le dossier.",
    dossierLinkedProperty: "Bien lié",
    addParcel: "Ajouter au cadastre",
    editParcel: "Modifier",
    noParcelsForProperty: "Aucune entrée cadastrale pour l'instant. Ajoutez la première ci-dessous.",
    parcelsForProperty: "Entrées cadastrales du bien",
    section: "Section",
    parcelNumber: "N° de parcelle",
    lieuDit: "Lieu-dit",
    commune: "Commune",
    surface: "Contenance",
    totalCadastralSurface: "Surface cadastrale totale",
    ref: "Réf",
    viewCards: "Vignettes",
    viewTable: "Tableau",
    noRoomsForProperty: "Aucune pièce mesurée pour l'instant. Ajoutez la première ci-dessous.",
    roomsForProperty: "Pièces mesurées pour ce bien",
    roomsMovedTitle: "Ouvrez un bien pour mesurer ses pièces",
    roomsMovedBody: "Les mesures sont désormais rattachées à chaque bien (Biens → ouvrir le bien → onglet Pièces). Chaque métrage reste lié à son bien et vous pouvez l'imprimer en un clic.",
    openProperties: "Ouvrir les biens",
    documents: "Documents",
    documentsHint: "Mandat, DPE, ERP, diagnostics, acte, ID, etc.",
    idDocuments: "Pièces d'identité",
    idDocumentsHint: "Passeport, permis de conduire, carte d'identité — restent privés.",
    addDocument: "Ajouter un document",
    addNote: "Ajouter une note",
    noteSubjectPlaceholder: "Sujet (facultatif) — ex. Appel au sujet de la toiture",
    noteBodyPlaceholder: "Que s'est-il passé ? Quelque chose à retenir…",
    noteBodyRequired: "Veuillez d'abord écrire une note",
    noteAdded: "Note ajoutée",
    followUpRequired: "Relance nécessaire",
    followUpOn: "Relance le",
    openBuyer: "Ouvrir l'acheteur",
    openSeller: "Ouvrir le vendeur",
    alreadyLinkedBuyer: "Déjà lié à un acheteur",
    alreadyLinkedSeller: "Déjà lié à un vendeur",
    roomsTitle: "Métrés des pièces",
    roomsSubtitle: "Longueur × largeur = surface, calculé automatiquement.",
    roomsSearchPlaceholder: "Rechercher des pièces…",
    addRoom: "Ajouter un métré", editRoom: "Modifier le métré",
    noRooms: "Aucun métré pour l'instant. Ajoutez votre première pièce lors d'une visite.",
    roomName: "Nom de la pièce", roomNamePlaceholder: "ex. Chambre principale",
    roomNameRequired: "Le nom de la pièce est requis",
    propertyRequired: "Veuillez choisir un bien", selectProperty: "Sélectionner un bien",
    allProperties: "Tous les biens",
    length: "Longueur", width: "Largeur", unit: "Unité",
    lengthWidthRequired: "La longueur et la largeur sont requises",
    calculatedArea: "Surface calculée",
    areaFormula: "Surface = Longueur × Largeur",
    manualOverrideActive: "Surface saisie manuellement — remplace L × l",
    manualAreaOverride: "Surface totale (saisie manuelle)",
    manualAreaHint: "Saisissez une valeur pour remplacer le calcul — utile pour les pièces irrégulières ou si vous avez déjà le chiffre du géomètre.",
    measurementDate: "Date du métré",
    totalAreaByProperty: "Surface totale mesurée par bien",
    pipelineTitle: "Pipeline des biens",
    pipelineSubtitle: "Faites glisser une carte entre les étapes pour faire avancer le dossier. L'IA suggère l'action suivante.",
    pipelineSearchPlaceholder: "Rechercher un bien, adresse, réf…",
    allStages: "Toutes les étapes", allNegotiators: "Tous les négociateurs", clearFilters: "Effacer les filtres",
    stageEmpty: "Déposez un bien ici", stageMoved: "Étape mise à jour",
    aiSuggestNext: "Suggérer l'action suivante", aiSuggestTitle: "Prochaine action",
    noAddress: "Bien sans nom", offersShort: "offres", loading: "Chargement",
    lostReasonPrompt: "Optionnel — raison de la perte du dossier (ex. autre agence, retrait, prix) :",
    completionDatePrompt: "Date de signature (AAAA-MM-JJ) :",
    offersTab: "Offres", offersTitle: "Offres sur ce bien",
    offersSubtitle: "Suivez chaque offre reçue. Accepter une offre bascule le bien en « Sous compromis » et refuse les autres.",
    addOffer: "Ajouter une offre", offerAdded: "Offre ajoutée",
    offerAmount: "Montant de l'offre", offerDate: "Date de réception",
    financialPosition: "Situation financière", chainPosition: "Situation de chaîne",
    proofOfFunds: "Justificatif de fonds reçu", noOffersYet: "Aucune offre pour l'instant sur ce bien.",
    selectBuyer: "Sélectionner un acheteur", buyer: "Acheteur",
    offerAmountRequired: "Veuillez saisir un montant",
    acceptOffer: "Accepter cette offre",
    acceptOfferConfirm: "Accepter l'offre de {amount} ? Le bien passera en « Sous compromis » et les autres offres seront refusées.",
    offerAccepted: "Offre acceptée — bien passé sous compromis.",
    offerStatus_new: "Nouvelle", offerStatus_negotiating: "En négociation",
    offerStatus_accepted: "Acceptée", offerStatus_rejected: "Refusée", offerStatus_withdrawn: "Retirée",
    fin_cash: "Comptant", fin_mortgage_agreed: "Prêt accordé", fin_mortgage: "Prêt en attente", fin_unknown: "Inconnu",
    finCash: "Comptant", finMortgageAgreed: "Prêt accordé", finMortgage: "Prêt en attente", finUnknown: "Inconnu",
    chain_no_chain: "Sans chaîne", chain_top_of_chain: "Haut de chaîne", chain_in_chain: "Dans une chaîne", chain_unknown: "Inconnu",
    chainNone: "Sans chaîne", chainTop: "Haut de chaîne", chainIn: "Dans une chaîne",
    kpiNewLeads: "Nouveaux prospects", kpiContactMade: "Contacts établis",
    kpiValuationsBooked: "Estimations planifiées", kpiValuationsCompleted: "Estimations réalisées",
    kpiAwaitingDecision: "En attente de décision", kpiMandatesSigned: "Mandats signés",
    kpiClosedWon: "Instructions obtenues", kpiLiveListings: "Biens en ligne",
    kpiViewingsToday: "Visites aujourd'hui", kpiActiveOffers: "Offres actives",
    kpiUnderOffer: "Sous compromis", kpiSoldThisMonth: "Vendus ce mois-ci", kpiClosedLost: "Perdus",
    search: "Rechercher...", add: "Ajouter", edit: "Modifier", delete: "Supprimer", save: "Enregistrer", preview: "Aperçu",
    cancel: "Annuler", copy: "Copier", copied: "Copié dans le presse-papiers", close: "Fermer",
    logout: "Déconnexion", login: "Connexion", email: "Email", password: "Mot de passe",
    welcomeBack: "Bon retour", loginSubtitle: "Votre assistante immobilière personnelle",
    continueWithGoogle: "Continuer avec Google",
    signInDescription: "Connectez-vous avec votre compte Google pour accéder à votre espace.",
    signInFooter: "Nouveau ici ? Votre compte sera créé automatiquement lors de votre première connexion.",
    signInTab: "Connexion", signUpTab: "Inscription",
    signIn: "Se connecter", signingIn: "Connexion...", or: "ou",
    yourName: "Votre nom", yourNamePlaceholder: "Julia Martin",
    createAccount: "Créer un compte", creatingAccount: "Création du compte...",
    accountCreated: "Bienvenue sur Julia AI",
    passwordHint: "8 caractères minimum", passwordTooShort: "Le mot de passe doit contenir au moins 8 caractères.",
    passwordsDontMatch: "Les mots de passe ne correspondent pas.",
    forgotPassword: "Mot de passe oublié ?",
    forgotTitle: "Réinitialiser le mot de passe", forgotSubtitle: "Saisissez votre email et nous vous enverrons un lien pour définir un nouveau mot de passe.",
    sendResetLink: "Envoyer le lien", sending: "Envoi...", back: "Retour",
    checkYourInbox: "Vérifiez votre boîte mail", resetSentBody: "Si un compte existe pour cet email, un lien de réinitialisation est en route. Le lien expire dans 1 heure.",
    backToSignIn: "Retour à la connexion",
    resetTitle: "Choisissez un nouveau mot de passe", resetSubtitle: "Définissez le mot de passe pour votre connexion.",
    newPassword: "Nouveau mot de passe", confirmPassword: "Confirmer le mot de passe",
    updatePassword: "Mettre à jour", updating: "Mise à jour...", passwordUpdated: "Mot de passe mis à jour",
    resetLinkInvalid: "Ce lien de réinitialisation est manquant ou invalide.",
    profile: "Profil", profileSubtitle: "Votre compte et signature email",
    googleAccount: "Compte Google", displayName: "Nom d'affichage",
    agentSignature: "Signature email", agentSignaturePlaceholder: "Julia Martin\nAgent immobilier indépendant\njulia@example.com · +33 6 12 34 56 78",
    agentSignatureHint: "Utilisé lorsque [AgentName] ou [AgentSignature] apparaît dans un modèle.",
    todaysAppointments: "Rendez-vous du jour", urgentFollowUps: "Relances urgentes",
    upcomingTasks: "Tâches à venir", noPendingTasks: "Aucune tâche en attente",
    noDueDate: "Sans échéance", viewAllTasks: "Voir toutes les tâches",
    missingDocuments: "Documents manquants", upcomingDecisions: "Décisions à venir",
    newEnquiries: "Nouvelles demandes", commissionPipeline: "Pipeline de commissions",
    live: "En vente", underOffer: "Sous offre", nothingToday: "Rien de prévu aujourd'hui",
    allClear: "Tout est à jour", noResults: "Aucun résultat",
    name: "Nom", phone: "Téléphone", country: "Pays", budget: "Budget", notes: "Notes",
    financePosition: "Financement", searchArea: "Secteur recherché", mustHaves: "Indispensables",
    dealBreakers: "Rédhibitoires", likesDislikes: "Aime / n'aime pas", nextAction: "Prochaine action",
    nextActionDate: "Date de prochaine action", autoTaskHint: "Une tâche avec cette échéance sera créée automatiquement",
    followUpDate: "Date de relance", addBuyer: "Ajouter un acheteur", editBuyer: "Modifier l'acheteur",
    cash: "Comptant", mortgage: "Crédit", propertyToSell: "Bien à vendre", unknown: "Inconnu",
    status: "Statut", lastContact: "Dernier contact", documentsChecklist: "Documents",
    linkedProperty: "Bien associé", addSeller: "Ajouter un vendeur", editSeller: "Modifier le vendeur",
    prospect: "Prospect", valuationBooked: "Estimation prévue", valuationCompleted: "Estimation faite",
    mandatePending: "Mandat en attente", sold: "Vendu",
    addProperty: "Ajouter un bien", editProperty: "Modifier le bien", priceNet: "Prix net vendeur",
    agencyFees: "Honoraires", priceTotal: "Prix FAI", propertyType: "Type",
    bedrooms: "Chambres", bathrooms: "Salles de bain", habitableSpace: "Surface habitable (m²)",
    landSize: "Terrain (m²)", outbuildings: "Dépendances", pool: "Piscine", gitePotential: "Potentiel gîte",
    heating: "Chauffage", drainage: "Assainissement", locationPublic: "Description publique",
    addressPrivate: "Adresse privée / accès", advertEn: "Annonce anglaise", advertFr: "Annonce française",
    photos: "Photos", floorplans: "Plans", uploadPhoto: "Ajouter une photo", uploadFloorplan: "Ajouter un plan",
    newViewing: "Nouveau retour de visite", editViewing: "Modifier la visite", buyer: "Acheteur", property: "Bien",
    dateTime: "Date et heure", generalReaction: "Réaction générale", liked: "Ce qu'ils ont aimé",
    concerns: "Réserves / inquiétudes", otherProperties: "Autres biens à visiter",
    decisionDate: "Date de décision", generateReport: "Générer le rapport de visite", generating: "Génération...",
    englishReport: "Rapport anglais", frenchReport: "Rapport français", hestiaNotes: "Notes Hestia",
    reportReady: "Rapport généré — tâche de suivi créée", viewReport: "Voir le rapport",
    aiTitle: "Générateur de rapports IA", aiSubtitle: "Collez vos notes, choisissez un format, Julia AI rédige",
    roughNotes: "Vos notes brutes", outputType: "Type de document", generate: "Générer",
    viewingReport: "Rapport de visite", sellerFollowUp: "Relance vendeur", buyerFollowUp: "Relance acheteur",
    propertyAdvert: "Annonce immobilière", frenchTranslation: "Traduction française",
    englishTranslation: "Traduction anglaise", whatsappMessage: "Message WhatsApp", formalEmail: "Email formel",
    result: "Résultat", addTemplate: "Ajouter un modèle", editTemplate: "Modifier le modèle",
    titleEn: "Titre (anglais)", titleFr: "Titre (français)", bodyEn: "Texte anglais", bodyFr: "Texte français",
    category: "Catégorie", addTask: "Ajouter une tâche", editTask: "Modifier la tâche", taskTitle: "Titre",
    templatesSubtitle: "Messages réutilisables enregistrés en base — jamais écrasés par les redéploiements",
    duplicate: "Dupliquer", duplicated: "Modèle dupliqué", updated: "Modifié",
    status_active: "Actif", status_draft: "Brouillon", status_archived: "Archivé",
    templateValidationTitle: "Le titre anglais est obligatoire",
    templateValidationBody: "Au moins une langue (EN ou FR) doit contenir du texte",
    confirmDeleteTemplate: "Supprimer le modèle « {name} » ? Cette action est définitive.",
    autoTranslateLabel: "Traduire automatiquement",
    translating: "Traduction...",
    translateFromEn: "Traduire vers FR",
    translateFromFr: "Traduire vers EN",
    translatedNotice: "Traduit en {tgt}",
    translateFailed: "Échec de la traduction",
    sourceLangHint: "Édition en {src} — la version {tgt} sera régénérée à l'enregistrement.",
    lang_en: "anglais", lang_fr: "français",
    systemDefault: "Modèle par défaut",
    systemTemplateHint: "Modifier un modèle par défaut crée une copie privée — l'original reste intact.",
    insertPlaceholder: "+ Insérer un champ",
    dueDate: "Échéance", priority: "Priorité", linkedRecord: "Fiche liée", linkedType: "Lier à",
    pending: "En cours", done: "Terminé", overdue: "En retard", high: "Haute", medium: "Moyenne", low: "Basse",
    none: "Aucun", viewing: "Visite", seller: "Vendeur", today: "Aujourd'hui", decisionBy: "Décision avant le",
    lead: "Prospect",
    saved: "Enregistré", deleted: "Supprimé", confirmDelete: "Supprimer cette fiche ?", required: "Champ obligatoire",
    viewingHistory: "Historique des visites", buyerMatches: "Acheteurs potentiels", noViewings: "Aucune visite enregistrée",
    firstName: "Prénom", surname: "Nom",
    forms: "Formulaires", formBuilder: "Créateur de formulaires", newForm: "Nouveau formulaire", fillForm: "Remplir",
    formName: "Nom du formulaire", addField: "Ajouter un champ", fieldLabel: "Libellé du champ", fieldType: "Type",
    requiredField: "Obligatoire", optionsHint: "Options (séparées par des virgules)", typeText: "Texte",
    typeTextarea: "Texte long", typeNumber: "Nombre", typeDate: "Date", typeDropdown: "Liste déroulante",
    typeCheckbox: "Case à cocher", typeFile: "Fichier",
    templatesTab: "Modèles de formulaires", submissionsTab: "Réponses", copySummary: "Copier le résumé",
    noSubmissions: "Aucune réponse pour le moment", submitForm: "Enregistrer la réponse",
    sellerDetailsCat: "Fiche vendeur", propertyDetailsCat: "Fiche bien", buyerCriteriaCat: "Critères acheteur",
    viewingFeedbackCat: "Retour de visite", customCat: "Personnalisé",
    rooms: "Pièces", roomMeasurements: "Métrés des pièces", addRoom: "Ajouter une pièce", editRoom: "Modifier la pièce",
    roomName: "Nom de la pièce", floorLevel: "Étage", shapeType: "Forme", ceilingHeight: "Hauteur sous plafond (m)",
    flooring: "Sol", windowsGlazing: "Fenêtres / vitrage", builtInStorage: "Rangements intégrés", condition: "État",
    sectionsLabel: "Sections de mesure", addSection: "Ajouter une section", sectionName: "Nom de la section",
    lengthM: "Longueur (m)", widthM: "Largeur (m)", areaLabel: "Surface", totalArea: "Surface totale",
    overrideArea: "Remplacer le total (m²)", sketchRoom: "Croquis", drawRoom: "Dessiner la pièce", finishRoom: "Terminer la pièce",
    undo: "Annuler", redo: "Rétablir", clearSketch: "Effacer", perimeter: "Périmètre", habitableArea: "Surface habitable",
    excludeHabitable: "Exclure de la surface habitable", needsVerification: "À vérifier",
    applyToProperty: "Appliquer à la fiche bien", copySchedule: "Copier le métré",
    roomSchedule: "Métré des pièces",
    tapToDraw: "Touchez ou cliquez pour placer chaque angle, puis Terminer la pièce",
    tapWallHint: "Touchez un mur pour saisir sa longueur", deleteCorner: "Supprimer l'angle",
    notClosed: "Les murs ne se referment pas — vérifiez les longueurs",
    sellingPoints: "Points forts", summary: "Résumé", descriptionLabel: "Description", marketing: "Marketing",
    generateAdvert: "Générer les annonces EN + FR (IA)", advertsGenerated: "Annonces générées à partir des pièces et points forts",
    saveFirst: "Enregistrez d'abord le bien",
    calendar: "Calendrier", todayBtn: "Aujourd'hui", addToCalendar: "Ajouter au calendrier",
    followUpEvent: "Relance", decisionEvent: "Décision", moreEvents: "autres", time: "Heure",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("julia-lang") || "en");
  const value = useMemo(() => {
    const switchLang = (l) => {
      setLang(l);
      localStorage.setItem("julia-lang", l);
    };
    const t = (key) => translations[lang][key] || translations.en[key] || key;
    return { lang, switchLang, t };
  }, [lang]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
